import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { API, useAuth } from '../context/AuthContext';
import { colors, fonts } from '../brand';
import Disclaimer from '../components/Disclaimer';
import SiteNav from '../components/SiteNav';
import Accordion from '../components/Accordion';
import SiteFooter from '../components/SiteFooter';

export default function Resources() {
  const { user } = useAuth();
  const isMember = user?.subscription_status === 'active';
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    axios.get(`${API}/api/content/resources`).then(res => {
      const sorted = (res.data.resources || [])
        .sort((a, b) => a.heading.localeCompare(b.heading))
        .map(cat => ({ ...cat, items: [...cat.items].sort((a, b) => a.name.localeCompare(b.name)) }));
      setCategories(sorted);
    }).catch(() => {});
  }, []);

  return (
    <div style={styles.page}>
      <Helmet>
        <title>Divorce Resources — Organizations, Hotlines & Tools | Elephants Answering</title>
        <meta name="description" content="A growing list of divorce resources built by the community — organizations, professionals, hotlines, and tools for women navigating divorce." />
      </Helmet>
      <SiteNav />
      <main style={styles.main}>
        <div style={styles.inner}>
          <h1 style={styles.title}>Resources</h1>
          <p style={styles.subtitle}>
            Organizations, professionals, hotlines, tools — a growing list, built by the herd. If something helped you, send it our way so it can help the next woman.<br /><br />All links are external; we're not affiliated with these organizations, professionals, hotlines, tools, etc.
          </p>

          <Accordion
            allowMultiple={true}
            items={categories.map(cat => ({
              label: cat.heading,
              children: (
                <div>
                  <p style={styles.catDesc}>{cat.description}</p>
                  <div style={styles.cards}>
                    {cat.items.map(item => (
                      <a
                        key={item.name}
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={styles.card}
                        onMouseEnter={e => e.currentTarget.style.borderTopColor = colors.rose}
                        onMouseLeave={e => e.currentTarget.style.borderTopColor = colors.peach}
                      >
                        <div style={styles.cardName}>{item.name}</div>
                        <p style={styles.cardDesc}>{item.desc}</p>
                        <span style={styles.cardLink}>Visit site →</span>
                      </a>
                    ))}
                  </div>
                </div>
              ),
            }))}
          />

          <div style={styles.directoryCallout}>
            <div>
              <p style={styles.directoryLabel}>Members Only</p>
              <p style={styles.directoryTitle}>50-State Divorce DIY Directory</p>
              <p style={styles.directoryDesc}>State-by-state filing info, court links, and DIY resources — all in one place.</p>
            </div>
            {isMember ? (
              <a href="/50-state-divorce-diy-directory.pdf" download style={styles.directoryBtn}>Download PDF →</a>
            ) : (
              <Link to="/register" style={styles.directoryBtn}>Join to Download →</Link>
            )}
          </div>

          <div style={styles.legalAidCallout}>
            <div>
              <p style={styles.glossaryTitle}>Free Legal Help</p>
              <p style={styles.glossaryDesc}>Pro bono lawyers, legal aid organizations, and court self-help centers.</p>
            </div>
            <Link to="/legal-aid" style={styles.glossaryBtn}>Legal Aid Guide →</Link>
          </div>

          <div style={styles.glossaryCallout}>
            <div>
              <p style={styles.glossaryTitle}>Divorce Glossary</p>
              <p style={styles.glossaryDesc}>40+ legal terms explained in plain English — no jargon.</p>
            </div>
            <Link to="/glossary" style={styles.glossaryBtn}>View Glossary →</Link>
          </div>

          <div style={styles.cta}>
            <p style={styles.ctaText}>Still have questions? Join women who've been through it.</p>
            <Link to="/register" style={styles.ctaBtn}>Join the Community</Link>
            <p style={styles.ctaTagline}>Free for Early Members</p>
          </div>
        </div>
      </main>
      <Disclaimer />
      <SiteFooter />
    </div>
  );
}

const styles = {
  page: { fontFamily: fonts.body, backgroundColor: colors.cream, minHeight: '100vh' },
  main: { padding: '64px 20px 80px' },
  inner: { maxWidth: '900px', margin: '0 auto' },
  eyebrow: { fontFamily: fonts.body, color: colors.rose, fontSize: '11px', letterSpacing: '2.5px', textTransform: 'uppercase', marginBottom: '12px' },
  title: { fontFamily: fonts.heading, color: colors.navy, fontSize: '36px', fontWeight: '600', marginBottom: '16px', letterSpacing: '0.3px' },
  subtitle: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '15px', lineHeight: '1.9', marginBottom: '40px' },
  glossaryCallout: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', backgroundColor: colors.navy, padding: '24px 32px', marginBottom: '20px' },
  glossaryLabel: { fontFamily: fonts.body, color: colors.peach, fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '4px' },
  glossaryTitle: { fontFamily: fonts.heading, color: colors.white, fontSize: '18px', fontWeight: '600', marginBottom: '6px' },
  glossaryDesc: { fontFamily: fonts.body, color: 'rgba(255,255,255,0.6)', fontSize: '13px', margin: 0 },
  glossaryBtn: { backgroundColor: 'transparent', border: `1px solid ${colors.peach}`, color: colors.peach, padding: '10px 24px', fontSize: '13px', fontFamily: fonts.body, fontWeight: '600', letterSpacing: '0.5px', whiteSpace: 'nowrap', textDecoration: 'none', display: 'inline-block' },
  legalAidCallout: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', backgroundColor: '#1a3a5c', padding: '24px 32px', marginBottom: '20px' },
  directoryCallout: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', backgroundColor: colors.rose, padding: '24px 32px', marginBottom: '20px', marginTop: '48px' },
  directoryLabel: { fontFamily: fonts.body, color: 'rgba(255,255,255,0.7)', fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '4px' },
  directoryTitle: { fontFamily: fonts.heading, color: colors.white, fontSize: '18px', fontWeight: '600', marginBottom: '6px' },
  directoryDesc: { fontFamily: fonts.body, color: 'rgba(255,255,255,0.8)', fontSize: '13px', margin: 0 },
  directoryBtn: { backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.6)', color: colors.white, padding: '10px 24px', fontSize: '13px', fontFamily: fonts.body, fontWeight: '600', cursor: 'pointer', letterSpacing: '0.5px', whiteSpace: 'nowrap', textDecoration: 'none' },
  catDesc: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '13px', lineHeight: '1.8', marginBottom: '24px', fontStyle: 'italic' },
  cards: { display: 'flex', flexDirection: 'column', gap: '16px' },
  card: { display: 'block', padding: '24px 28px', backgroundColor: colors.white, border: `1px solid ${colors.peach}`, borderTop: `3px solid ${colors.peach}`, textDecoration: 'none', transition: 'border-top-color 0.15s ease' },
  cardName: { fontFamily: fonts.heading, color: colors.navy, fontSize: '15px', fontWeight: '600', marginBottom: '8px', letterSpacing: '0.3px' },
  cardDesc: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '13px', lineHeight: '1.8', margin: '0 0 12px' },
  cardLink: { fontFamily: fonts.body, color: colors.rose, fontSize: '12px', fontWeight: '600', letterSpacing: '0.5px' },
  cta: { marginTop: '16px', padding: '48px 40px', backgroundColor: colors.navy, textAlign: 'center' },
  ctaText: { fontFamily: fonts.heading, color: colors.white, fontSize: '18px', marginBottom: '20px', fontWeight: '500' },
  ctaBtn: { backgroundColor: colors.rose, color: colors.white, textDecoration: 'none', padding: '14px 36px', fontSize: '14px', fontWeight: '600', fontFamily: fonts.body, letterSpacing: '0.5px', display: 'inline-block' },
  ctaTagline: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '12px', marginTop: '12px' },
};
